import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ContactDetails,
  ConversationTypeEnum,
  DealerCommunicationDetails,
  Environment,
  IframeConfig,
  LeadStatusType,
  OccConfig,
  ReplyOption,
  SystemOfRecord
} from "@coxauto-ui/cxm/communications/interfaces";
import { ConversationPane } from "./conversation-pane";
import {
  UserConsumerProfileResponse,
  getContactDetails,
  getCrmGenAIDealer,
  getCrmGenAIDealerRequest,
  getDealerCommunicationDetailsByConsumerId,
  getLeadStatusType,
  getUserConsumerProfile,
  isResponseError
} from "@coxauto-ui/cxm/communications/api";
import { ConversationContext } from "./context";
import { TagManager } from "@coxauto-ui/core-third-party-ga4";
import authClient from "@coxauto-ui/core-auth-get-auth-client-customer-conversation";
import { InterstateThemeProvider } from "@interstate/components/InterstateThemeProvider";
import { interstateThemeRegistry } from "@interstate/components/InterstateTheming/themes/interstateThemeRegistry";
import { ErrorMessages } from "@coxauto-ui/cxm/communications/util";
import type { ErrorDetails } from "./components/error-alert";
import { extractErrorDetails } from "./utils/extract-error-details";
import { CoreLdProvider } from "@coxauto-ui/core/ld-provider";
import { CenteredSpinner } from "@coxauto-ui/core/components";

export interface ConversationProps {
  customerId: string;
  customerSystem?: SystemOfRecord;
  /** Determines which Dealer Org is utilizing the inbox */
  selectedCommonOrgId?: string;
  /** Determines which lead to associate communications to */
  selectedLeadId?: string;
  /** Determines which repair order to associate communications to */
  selectedRepairOrderId?: string;
  /** Determines which service appointment to associate communications to */
  selectedServiceAppointmentId?: string;
  /** Determines which task to associate communications to */
  selectedTaskId?: string;
  /** Determines which template to use for communications*/
  selectedTemplateId?: string;
  /** When `true`, displays the title above the customer conversation*/
  showCustomerInfo?: boolean;
  /** Title override to display when `showCustomerInfo` is true. Defaults to customer name when undefined */
  title?: string;
  occConfig: OccConfig;
  getAuthOverride?: () => Promise<string>;
  env?: Environment;
  /** Iframe configuration for postMessage communication (internal use) */
  iframeConfig?: IframeConfig;
}

const supportedGenerativeAIDomains = [
  "localhost",
  "qa-vinsolutions.app.coxautoinc.com",
  "vinsolutions.app.coxautoinc.com",
  "crm.app.coxautoinc.com",
  "dev-crm.app.coxautoinc.com",
  "uat-crm.app.coxautoinc.com",
  "integration-crm.app.coxautoinc.com"
];

export function CustomerConversationApp({
  customerId,
  customerSystem,
  selectedCommonOrgId,
  selectedLeadId,
  selectedRepairOrderId,
  selectedServiceAppointmentId,
  selectedTaskId,
  selectedTemplateId,
  showCustomerInfo = true,
  title,
  occConfig = {
    username: "",
    disableReply: false,
    defaultTab: undefined,
    messageContent: null
  },
  getAuthOverride,
  env = Environment.none,
  iframeConfig
}: ConversationProps) {
  const [customerName, setCustomerName] = useState("");
  const [replyOptions, setReplyOptions] = useState<ReplyOption[]>([]);
  const [activeListTab, setActiveListTab] = useState(ConversationTypeEnum.ALL);
  const [error, setError] = useState("");
  const [contactDetailsError, setContactDetailsError] = useState("");
  const [contactDetailsErrorDetails, setContactDetailsErrorDetails] = useState<
    ErrorDetails | undefined
  >(undefined);
  const [disableReply, setDisableReply] = useState(occConfig.disableReply);
  const [defaultTab, setDefaultTab] = useState(occConfig.defaultTab);
  const [defaultMessageContent, setDefaultMessageContent] = useState<
    string | null | undefined
  >(occConfig.messageContent);
  const [isGenerativeAIEnabled, setIsGenerativeAIEnabled] = useState(false);
  const [hasActiveTask, setHasActiveTask] = useState(false);
  const [getUserName, setGetUserName] = useState<() => string>(() => {
    return () => {
      return occConfig.username;
    };
  });
  const [contactDetails, setContactDetails] = useState<ContactDetails | null>(
    null
  );
  const [dealerCommunicationDetails, setDealerCommunicationDetails] =
    useState<DealerCommunicationDetails | null>(null);
  const [autoLeadStatus, setAutoLeadStatus] = useState<LeadStatusType>(
    LeadStatusType.NONE
  );
  const [leadStatusError, setLeadStatusError] = useState(false);
  const [userConsumerProfile, setuserConsumerProfile] =
    useState<UserConsumerProfileResponse | null>(null);
  const [isUserProfileLoading, setIsUserProfileLoading] = useState(true);
  const [vinDealerId, setVinDealerId] = useState<number>(0);
  const [commonOrgId, setCommonOrgId] = useState<string>("");
  useEffect(() => {
    if (getAuthOverride) {
      authClient.getAuth = getAuthOverride;
    }
  });

  const setCustomerConversationContext = useCallback(async () => {
    const request = {
      consumerId: customerId,
      consumerRecordSystem: customerSystem || SystemOfRecord.COMMON
    };

    let resolvedContactDetails: ContactDetails | null = null;
    try {
      resolvedContactDetails = await getContactDetails(request, env);
      setReplyOptions(resolvedContactDetails.replyOptions);
      setCustomerName(resolvedContactDetails.name.fullName);
      setContactDetails(resolvedContactDetails);
      setContactDetailsError("");
    } catch (_err: unknown) {
      if (isResponseError(_err) && _err.statusCode === 403) {
        setError(_err.message ?? ErrorMessages.FORBIDDEN_ERROR);
        return;
      }
      setContactDetailsError(ErrorMessages.CONTACT_DETAILS_UNAVAILABLE);
      setContactDetailsErrorDetails({
        ...extractErrorDetails(_err),
        consumerId: customerId
      });
    }

    const dealerCommunicationDetails =
      await getDealerCommunicationDetailsByConsumerId(request, env);
    setDealerCommunicationDetails(dealerCommunicationDetails);
  }, [customerId, customerSystem, env]);

  useEffect(() => {
    setError("");
    setContactDetailsError("");
    setContactDetailsErrorDetails(undefined);
    setLeadStatusError(false);
  }, [customerId]);

  useEffect(() => {
    setCustomerConversationContext();
  }, [setCustomerConversationContext]);

  useEffect(() => {
    const hostToCheck = iframeConfig?.hostname || window.location.hostname;

    if (supportedGenerativeAIDomains.includes(hostToCheck)) {
      (async function () {
        try {
          const request: getCrmGenAIDealerRequest = {
            consumerId: customerId,
            consumerRecordSystem: customerSystem || SystemOfRecord.COMMON
          };

          const response = await getCrmGenAIDealer(request, env);

          if (response?.enabled) {
            setIsGenerativeAIEnabled(true);
          } else {
            setIsGenerativeAIEnabled(false);
          }
        } catch (error: unknown) {
          // silently fail the buttons. ha ha ha.
        }
      })();
    }
  }, [customerId, customerSystem, env, iframeConfig?.hostname]);

  useEffect(() => {
    TagManager.event({
      event: "productEvent",
      eventName: "Conversation Opened",
      eventProperties: {
        packagedBusinessCapability: "OCC Inbox",
        commonCustomerId: customerId,
        communicationChannel: undefined,
        replyContext: undefined,
        errorMessage: undefined
      }
    });

    return () => {
      TagManager.event({
        event: "productEvent",
        eventName: "Conversation Closed",
        eventProperties: {
          packagedBusinessCapability: "OCC Inbox",
          commonCustomerId: customerId,
          communicationChannel: undefined,
          replyContext: undefined,
          errorMessage: undefined
        }
      });
    };
  }, [customerId]);

  useEffect(() => {
    setDisableReply(occConfig.disableReply);
    setGetUserName(() => () => occConfig.username);
    setDefaultTab(occConfig.defaultTab);
    setDefaultMessageContent(occConfig.messageContent);
  }, [occConfig]);

  // Initialize hasActiveTask based on selectedTaskId and defaultTab
  useEffect(() => {
    const taskId = selectedTaskId || null;
    const isActive = !!(defaultTab && taskId !== null && Number(taskId) > 0);
    setHasActiveTask(isActive);
  }, [selectedTaskId, defaultTab]);

  const completeTask = useCallback(() => {
    setHasActiveTask(false);
  }, []);

  // Fetch user profile using API
  useEffect(() => {
    const fetchUserProfile = async () => {
      const request = {
        consumerId: customerId,
        customerSystem: customerSystem || SystemOfRecord.COMMON
      };
      try {
        const response = await getUserConsumerProfile(request, env);
        setuserConsumerProfile(response);
        setIsUserProfileLoading(false);
        setVinDealerId(response.activeConsumerVinDealerId);
        setCommonOrgId(response.activeConsumerCommonOrgId);
      } catch (error) {
        if (isResponseError(error) && error.statusCode === 403) {
          setError(error.message ?? ErrorMessages.FORBIDDEN_ERROR);
          setIsUserProfileLoading(false);
          return;
        }
        console.error(
          "[CustomerConversationApp] fetchUserProfile: Error occurred:",
          error
        );
        setIsUserProfileLoading(false);
      }
    };
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, customerSystem, env]);

  const providerValue = useMemo(() => {
    const taskId = selectedTaskId || null;

    return {
      activeListTab,
      autoLeadId: selectedLeadId || null,
      repairOrderId: selectedRepairOrderId || null,
      autoLeadStatus,
      customerId,
      customerSystem: customerSystem || SystemOfRecord.COMMON,
      customerName,
      commonOrgId: selectedCommonOrgId || commonOrgId,
      dealerCommunicationDetails,
      disableReply,
      defaultTab,
      defaultMessageContent,
      env,
      error,
      contactDetailsError,
      contactDetailsErrorDetails,
      leadStatusError,
      hasActiveTask,
      isGenerativeAIEnabled,
      replyOptions,
      showCustomerInfo,
      title,
      taskId,
      templateId: selectedTemplateId || null,
      serviceAppointmentId: selectedServiceAppointmentId || null,
      contactDetails,
      occConfig,
      vinDealerId,
      bridgeUserId: userConsumerProfile?.userProfile?.bridgeUserId || null,
      getUserName,
      setActiveListTab,
      setError,
      setDefaultMessageContent,
      completeTask,
      refreshContactDetails: setCustomerConversationContext,
      inboxMessageHandler: iframeConfig?.handler,
      isIframeMode: iframeConfig?.isEnabled ?? false
    };
  }, [
    activeListTab,
    autoLeadStatus,
    customerId,
    customerName,
    customerSystem,
    dealerCommunicationDetails,
    disableReply,
    defaultTab,
    defaultMessageContent,
    env,
    error,
    contactDetailsError,
    contactDetailsErrorDetails,
    leadStatusError,
    hasActiveTask,
    isGenerativeAIEnabled,
    replyOptions,
    selectedCommonOrgId,
    selectedLeadId,
    selectedRepairOrderId,
    selectedTaskId,
    selectedTemplateId,
    selectedServiceAppointmentId,
    showCustomerInfo,
    title,
    contactDetails,
    occConfig,
    vinDealerId,
    userConsumerProfile,
    getUserName,
    setActiveListTab,
    setError,
    setDefaultMessageContent,
    completeTask,
    setCustomerConversationContext,
    iframeConfig
  ]);

  useEffect(() => {
    (async () => {
      // If we didn't have a valid lead to begin with, don't make the call.
      if (providerValue.autoLeadId && providerValue.autoLeadId !== "0") {
        try {
          const leadStatus = await getLeadStatusType(
            env,
            Number(providerValue.autoLeadId)
          );
          setAutoLeadStatus(leadStatus);
        } catch (_err: unknown) {
          setAutoLeadStatus(LeadStatusType.NONE);
          setLeadStatusError(true);
        }
      } else {
        setAutoLeadStatus(LeadStatusType.NONE);
      }
    })();
  }, [providerValue.autoLeadId, env]);

  const ldUser = useMemo(() => {
    return {
      key: userConsumerProfile?.userProfile?.bridgeUserId || "",
      custom: {
        vinDealerId: userConsumerProfile?.activeConsumerVinDealerId || "",
        commonOrgId: userConsumerProfile?.activeConsumerCommonOrgId || ""
      }
    };
  }, [userConsumerProfile]);

  return !isUserProfileLoading ? (
    <CoreLdProvider module={"cxm"} user={ldUser}>
      <InterstateThemeProvider
        applicationName="test"
        scopeName="test"
        themeRegistries={[interstateThemeRegistry]}
      >
        <ConversationContext.Provider value={providerValue}>
          <ConversationPane />
        </ConversationContext.Provider>
      </InterstateThemeProvider>
    </CoreLdProvider>
  ) : (
    <InterstateThemeProvider
      applicationName="test"
      scopeName="test"
      themeRegistries={[interstateThemeRegistry]}
    >
      <CenteredSpinner
        fullHeight
        id="customer-conversation-app-loading-indicator"
      />
    </InterstateThemeProvider>
  );
}

export default CustomerConversationApp;
